import { FocusEvent, ReactNode, forwardRef } from "react";
import { Icon, Typography } from "@/components/atoms";
import { clsx } from "@/utils";

interface BookingLocationInputProps {
  value: string;
  onChange: (text: string) => void;
  onFocus?: () => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  renderIcon?: () => JSX.Element;
  /** Right-aligned element inside the input (e.g. a clear ✕ while typing, or a favourite ☆ once set). */
  trailing?: ReactNode;
  /** Primary-coloured helper text shown beneath the input while `active`. */
  infoMessage?: string;
  /** Whether this input is the booking sheet's active field (shows the helper text). */
  active?: boolean;
  className?: string;
}

/**
 * A booking-sheet address input. Unlike LocationAddressAutocomplete it renders NO results of its
 * own — the panel shows one shared results surface (default suggestions or live search) for
 * whichever input is active, so this is a plain controlled input the panel can focus imperatively.
 */
export const BookingLocationInput = forwardRef<HTMLInputElement, BookingLocationInputProps>(
  ({ value, onChange, onFocus, onBlur, placeholder, renderIcon, trailing, infoMessage, active = false, className }, ref) => (
    <div className={clsx("w-full", className)}>
      <div className="relative">
        <input
          ref={ref}
          type="text"
          className={clsx(
            "h-12 w-full rounded-lg border-neutral-mid-gray pl-10 text-base text-neutral-black placeholder-neutral-silver-gray focus:border-primary focus:shadow-input focus:ring-0",
            // View-only (disabled via the panel's fieldset): make it obviously non-interactive.
            "disabled:cursor-not-allowed disabled:bg-neutral-surface-gray disabled:text-neutral-silver-gray",
            trailing ? "pr-12" : ""
          )}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          autoComplete="off"
        />
        <span className="absolute inset-y-0 left-0 flex items-center px-3">
          {renderIcon ? renderIcon() : <Icon name="SearchNormal1" size="sm" className="text-neutral-silver-gray" />}
        </span>
        {trailing && <span className="absolute inset-y-0 right-0 flex items-center pr-3">{trailing}</span>}
      </div>
      {infoMessage && active && (
        <Typography variant="small-regular" className="mt-1 block text-primary">
          {infoMessage}
        </Typography>
      )}
    </div>
  )
);
BookingLocationInput.displayName = "BookingLocationInput";
